/**
 * WINDOW_REGISTRY — the static catalogue of every window the engine manages.
 *
 * Pure data + types, ZERO React. This is the "what windows exist" half of the
 * engine; WindowManagerContext owns runtime state ("which are open") and
 * content.ts owns the id→component binding ("what each renders").
 *
 * Phase 1: the 24 entries are the 🛠 debug-tools kit (kind:'tool'), ported
 * verbatim from the old `ui/debug/registry.ts` DEBUG_TOOLS. Each carries:
 *   - category + defaultPosition/defaultSize (unchanged from the kit)
 *   - `urlSeed`: the query-param names that seed it OPEN — derived 1:1 from the
 *     old seedFromUrl param table so URL seeding stays byte-identical.
 *
 * Later phases add gameplay/diagnostic entries; the discriminated union makes
 * that a pure data change.
 *
 * `WindowId` (in types.ts) is HAND-WRITTEN, not derived — the tsc drift guard.
 */

import type { WindowDef } from './types';
import { GM_WINDOW_DEFS } from './registry.gm';
import { SOCIAL_WINDOW_DEFS } from './registry.social';

export const WINDOW_REGISTRY: readonly WindowDef[] = [
  {
    kind: 'tool',
    id: 'latency',
    label: 'Latency',
    icon: '⚡',
    description: 'P1/P2/P3+5/P6 phase timings, clock sync, sparkline + spike markers',
    // First column of the canonical 1600x1000 netcode VQA layout. Keep it
    // clear of the fixed player frame at x=16..236, y=16..156.
    defaultPosition: { x: 250, y: 20 },
    defaultSize: { width: 280, height: 260 },
    category: 'netcode',
    urlSeed: ['latency'],
  },
  {
    kind: 'tool',
    id: 'netTraffic',
    label: 'Net Traffic',
    icon: '📊',
    description: 'Cumulative msgs/bytes in+out, rates, top message types',
    defaultPosition: { x: 250, y: 300 },
    defaultSize: { width: 280, height: 240 },
    category: 'netcode',
    urlSeed: ['net', 'netshow'],
  },
  {
    kind: 'tool',
    id: 'netobsSparkline',
    label: 'Packet Sparkline',
    icon: '📈',
    description: '30s bytes/sec history sparklines (↑out + ↓in)',
    defaultPosition: { x: 16, y: 620 },
    defaultSize: { width: 280, height: 200 },
    category: 'netcode',
    urlSeed: ['netobs'],
  },
  {
    kind: 'tool',
    id: 'netSpike',
    label: 'Spike Analyzer',
    icon: '🚨',
    description: 'Spike severity tiers + offender breakdown vs rolling 30s baseline',
    // Starts below the fixed center mode banner (roughly y=64..105).
    defaultPosition: { x: 550, y: 110 },
    defaultSize: { width: 340, height: 320 },
    category: 'netcode',
    urlSeed: ['spikes'],
  },
  {
    kind: 'tool',
    id: 'netcodeAlloc',
    label: 'Alloc & Bandwidth',
    icon: '🧮',
    description: 'Live graphs — bandwidth out/in, pkts/s, JS heap, top allocation movers (30s rolling)',
    defaultPosition: { x: 320, y: 420 },
    defaultSize: { width: 340, height: 320 },
    category: 'netcode',
    urlSeed: ['alloc'],
  },
  {
    kind: 'tool',
    id: 'tickBadge',
    label: 'Server Tick',
    icon: '⏱',
    description: 'Server tick budget (mean/max vs 16.67ms) + hottest subsystems',
    defaultPosition: { x: 320, y: 420 },
    defaultSize: { width: 280, height: 160 },
    category: 'perf',
    urlSeed: ['tick'],
  },
  {
    kind: 'tool',
    id: 'statusBar',
    label: 'Status Bar',
    icon: '📡',
    description: 'Server connection, registered services, dead events, error count, build SHA',
    defaultPosition: { x: 200, y: 4 },
    defaultSize: { width: 640, height: 30 },
    category: 'debug',
    urlSeed: ['status'],
  },
  {
    kind: 'tool',
    id: 'debugInfo',
    label: 'Debug Info',
    icon: '🐛',
    description: 'Live FPS, entity + mob counts, player position, camera scroll/zoom',
    defaultPosition: { x: 16, y: 840 },
    defaultSize: { width: 220, height: 140 },
    category: 'debug',
    urlSeed: ['debuginfo'],
  },
  {
    kind: 'tool',
    id: 'teleport',
    label: 'Teleport Bar',
    icon: '🚀',
    description: '5×4 grid of test-teleport buttons (positions 1-20, X-100 spacing)',
    defaultPosition: { x: 720, y: 80 },
    defaultSize: { width: 300, height: 200 },
    category: 'world',
    urlSeed: ['teleport'],
  },
  {
    kind: 'tool',
    id: 'eventRate',
    label: 'Event Rate',
    icon: '⏲',
    description: 'EventBus emits/sec + per-entity render counts. Flags double-fires + double-renders.',
    defaultPosition: { x: 720, y: 320 },
    defaultSize: { width: 380, height: 460 },
    category: 'perf',
    urlSeed: ['eventrate'],
  },
  {
    kind: 'tool',
    id: 'lagRootCause',
    label: 'Lag Root Cause',
    icon: '🎯',
    description: 'Single-sentence diagnosis combining tick budget + snap rate + spike tier + payload sizes',
    defaultPosition: { x: 24, y: 16 },
    defaultSize: { width: 440, height: 120 },
    category: 'netcode',
    urlSeed: ['lagcause'],
  },
  {
    kind: 'tool',
    id: 'positionTracker',
    label: 'Position Tracker',
    icon: '📍',
    description: 'Per-source counter of every write to player.position. Reveals hidden mutators of the client-owned position. Default OFF (zero overhead); enable via checkbox or ?postrack=1.',
    defaultPosition: { x: 24, y: 360 },
    defaultSize: { width: 420, height: 360 },
    category: 'debug',
    urlSeed: ['postrack'],
  },
  {
    kind: 'tool',
    id: 'frameJitter',
    label: 'Frame Jitter',
    icon: '🎞',
    description: 'rAF gap histogram + percentiles (p50/p95/p99/max). Pinpoints frame-rendering jitter sources separate from netcode. Default OFF; enable via checkbox or ?framejitter=1.',
    defaultPosition: { x: 24, y: 740 },
    defaultSize: { width: 420, height: 380 },
    category: 'perf',
    urlSeed: ['framejitter'],
  },
  {
    kind: 'tool',
    id: 'smoothnessDashboard',
    label: 'Smoothness Dashboard',
    icon: '📈',
    description: 'Unified verdict (server + netcode + frame pipeline). Polls /api/diag/* every 3s. Same data as the dj_h2000_smoothness_dashboard MCP probe — visible in-game.',
    defaultPosition: { x: 500, y: 16 },
    defaultSize: { width: 360, height: 280 },
    category: 'perf',
    urlSeed: ['smoothness'],
  },
  {
    kind: 'tool',
    id: 'interpTuning',
    label: 'Interp Tuning',
    icon: '🎛',
    description: 'Live remote-entity interpolation + mob-bump feel: presets (maple-snappy / wow-smooth / linear-baseline) + per-type render-lag/mode/clamp + bump sliders. Tunes the running game (no reload). Smoothness arc, 2026-05-29.',
    defaultPosition: { x: 500, y: 320 },
    defaultSize: { width: 360, height: 420 },
    category: 'netcode',
    urlSeed: ['interptuning'],
  },
  {
    kind: 'tool',
    id: 'posDrift',
    label: 'Pos Drift',
    icon: '📐',
    description: 'Live rolling chart of server↔client player position drift. Samples dx/dy/distance @ 20Hz over a 5s window. Peak readout flags the worst snap in the window. Companion to scripts/qa/pos-drift-recorder/ which captures the same data offline.',
    defaultPosition: { x: 550, y: 450 },
    defaultSize: { width: 420, height: 230 },
    category: 'netcode',
    urlSeed: ['posdrift'],
  },
  {
    kind: 'tool',
    id: 'sceneFxTuner',
    label: 'Scene FX',
    icon: '🌗',
    description: 'Live tuner for the modular atmosphere layers (Scene Effects v2): scene-mood intensity dial, per-layer enable toggles, modulator readout, and Copy-config JSON to paste into a preset. Edits + re-applies the running scene (no redeploy).',
    defaultPosition: { x: 280, y: 80 }, // clear of the top-left player HUD card
    defaultSize: { width: 340, height: 380 },
    category: 'world',
    urlSeed: ['scenefx'],
  },
  {
    kind: 'tool',
    id: 'systemHealth',
    label: 'System Health',
    icon: '🩺',
    description: 'Live health of every registered game system (SystemHealthRegistry): status, last-tick age, error counts + stack trace. Polls 1Hz. Lifted from the buried DevTools → Connections → Show Health toggle (dev-tools modernization arc, 2026-05-29).',
    defaultPosition: { x: 24, y: 80 },
    defaultSize: { width: 380, height: 360 },
    category: 'debug',
    urlSeed: ['syshealth'],
  },
  {
    kind: 'tool',
    id: 'levelDiag',
    label: 'Level Diagnostics',
    icon: '🗺️',
    description: 'Read-only level/geometry/spawn inspector — current level info, spawn points, platforms, + log/export actions. Polls MapSystem @1Hz. Migrated off the UIContext showLevelDiagnostics flag into the kit (2026-05-29).',
    defaultPosition: { x: 460, y: 80 },
    defaultSize: { width: 460, height: 420 },
    category: 'world',
    urlSeed: ['leveldiag'],
  },
  {
    kind: 'tool',
    id: 'perfGraph',
    label: 'Performance Graph',
    icon: '📈',
    description: 'Live FPS sparkline (canvas, per-frame performance.now() deltas; 60 FPS full-height, mid-line ≈30).',
    defaultPosition: { x: 24, y: 460 },
    defaultSize: { width: 360, height: 220 },
    category: 'perf',
    urlSeed: ['perfgraph'],
  },
  {
    kind: 'tool',
    id: 'physicsSafety',
    label: 'Physics Safety',
    icon: '⚠️',
    description: 'Collision-failure monitor — reads the CollisionSafetyMonitor ring (1Hz) + clear button. NOTE (DEBUG-SAFETY-WIRE-01): the ring currently has no producer, so this window reports "NOT WIRED — no data" rather than a pass. Live physics safety is covered by systems/physics/SafetyMonitor. Pairs with the Physics Diag tool.',
    defaultPosition: { x: 460, y: 460 },
    defaultSize: { width: 380, height: 320 },
    category: 'debug',
    urlSeed: ['physicssafety'],
  },
  {
    kind: 'tool',
    id: 'mpDiagnostics',
    label: 'MP Diagnostics',
    icon: '🛰️',
    description: 'Schema↔render distance, lifecycle log, remote entity state. Polls the multiplayer-diagnostics runtime. (Migrated off the UIContext bridge into the kit, 2026-05-30.)',
    defaultPosition: { x: 990, y: 170 },
    defaultSize: { width: 390, height: 430 },
    category: 'netcode',
    urlSeed: ['mpdiag'],
  },
  {
    kind: 'tool',
    id: 'physicsDiag',
    label: 'Physics Diag',
    icon: '⚙️',
    description: 'Kernel/physics/input checkpoints — heartbeat, PhysicsSystem TPS, gravity, character velocity/position, grounded. (Migrated off the UIContext bridge into the kit, 2026-05-30.)',
    defaultPosition: { x: 370, y: 50 },
    defaultSize: { width: 350, height: 400 },
    category: 'debug',
    urlSeed: ['physicsdiag'],
  },
  {
    kind: 'tool',
    id: 'renderingDiag',
    label: 'Rendering Diag',
    icon: '🎨',
    description: 'Rendering-pipeline checkpoints — kernel/systems, map/level, platforms, camera, player sprite + live FPS/scene. (Migrated off the UIContext bridge into the kit, 2026-05-30.)',
    defaultPosition: { x: 10, y: 50 },
    defaultSize: { width: 350, height: 400 },
    category: 'debug',
    urlSeed: ['renderdiag'],
  },
  {
    kind: 'tool',
    id: 'authorityOverlay',
    label: 'Authority Overlay',
    icon: '🔵',
    description: 'Scene-wide authority halos (LOCAL/REMOTE/BOT/NPC) + color legend. Opening the tool enables the halos; closing it disables them. (Migrated off the UIContext bridge into the kit, 2026-05-30.)',
    defaultPosition: { x: 24, y: 560 },
    defaultSize: { width: 260, height: 170 },
    category: 'netcode',
    urlSeed: ['netobsOverlay'],
  },
  {
    kind: 'tool',
    id: 'projectileTracker',
    label: 'Projectile Tracker',
    icon: '🚀',
    description: 'Live table of active projectiles (id/type/pos/vel/target/homing) from ProjectileSystem, 10Hz. Also summoned by the /tracker chat command + DEV-BAR button (both via debugToolsBridge). (Migrated off the UIContext bridge into the kit, 2026-05-30.)',
    defaultPosition: { x: 24, y: 740 },
    defaultSize: { width: 400, height: 300 },
    category: 'abilities',
    urlSeed: ['tracker'],
  },
  // (audioDebug retired 2026-07-11: the Audio Lab bench folded into the 🔊
  //  gmSoundDebug card's collapsible section — one dedicated sound card,
  //  'sound' category. Its ?soundDebug= url seed moved with it.)

  // ── diagnostic kind (Phase 3) ──────────────────────────────────────
  // Folded from the standalone DiagnosticsContext-flag panels. These render
  // on EVERY surface (not debug-gated like tool windows) so DJ can watch
  // live combat/netcode on /brawl etc. The `id` strings deliberately
  // produce the SAME persisted ToolWindow geometry key the panels used
  // before the fold (WindowHost maps diagnostic ids to `toolwindow_<oldId>`),
  // so saved positions survive — see WindowHost.toolWindowId().
  {
    kind: 'diagnostic',
    id: 'targetAbilityCheck',
    label: 'Target Ability Check',
    icon: '🎯',
    description: 'Live gate readout (combat-enable / range / mana / cooldown / GCD) for the selected ability vs the current target. Auto-follows your last cast. (?devPanelsOn=1)',
    defaultPosition: { x: 20, y: 100 },
    defaultSize: { width: 320, height: 320 },
    minSize: { width: 280, height: 220 },
    category: 'abilities',
    zIndex: 500,
    urlSeed: ['devPanelsOn'],
  },
  {
    kind: 'diagnostic',
    id: 'abilityLifecycle',
    label: 'Ability Lifecycle',
    icon: '📊',
    description: 'Per-cast timeline of cast→VFX→combat→projectile lifecycle events with timing breakdown + anomaly flags. (?devPanelsOn=1)',
    defaultPosition: { x: 1060, y: 100 },
    defaultSize: { width: 440, height: 520 },
    minSize: { width: 320, height: 240 },
    maxSize: { width: 720, height: 800 },
    category: 'abilities',
    zIndex: 500,
    urlSeed: ['devPanelsOn'],
  },
  {
    kind: 'diagnostic',
    id: 'visualLifecycle',
    label: 'Visual Lifecycle',
    icon: '🎨',
    description: 'Stream of visual (VFX/FCT) events with duplicate detection — answers "did this VFX fire twice?". (?devPanelsOn=1)',
    defaultPosition: { x: 1060, y: 100 },
    defaultSize: { width: 440, height: 520 },
    minSize: { width: 320, height: 240 },
    maxSize: { width: 720, height: 800 },
    category: 'abilities',
    zIndex: 500,
    urlSeed: ['devPanelsOn'],
  },
  {
    kind: 'diagnostic',
    id: 'targetLifecycle',
    label: 'Target Lifecycle',
    icon: '🎯',
    description: 'What your TARGET is doing to you — mob attack diagnostics (distance / range / damage / interval gates) from server mobAttackDiagnostic broadcasts. (?devPanelsOn=1 · F4)',
    defaultPosition: { x: 1060, y: 640 },
    defaultSize: { width: 440, height: 420 },
    minSize: { width: 320, height: 220 },
    maxSize: { width: 720, height: 800 },
    category: 'abilities',
    zIndex: 500,
    urlSeed: ['devPanelsOn'],
  },
  {
    kind: 'diagnostic',
    id: 'abilityTestRig',
    label: 'Ability Test Rig',
    icon: '🧪',
    description: 'Active ability-driver panel — overrides + hotkey-slot casting for QA. (F3)',
    defaultPosition: { x: 20, y: 440 },
    defaultSize: { width: 460, height: 620 },
    minSize: { width: 360, height: 320 },
    maxSize: { width: 720, height: 900 },
    category: 'abilities',
    zIndex: 500,
  },
  {
    kind: 'diagnostic',
    id: 'abilityGrounds',
    label: 'Ability Grounds',
    icon: '🎯',
    description: 'Deterministic visual lab — free class casts, authored range targets, scenario lanes, and integrated render truth. Auto-opens on /abilitygrounds. (?grounds=1)',
    // Clear the fixed player HUD (x=16..236) so the entire drag handle is
    // reachable. Geometry resets each visit for reproducible captures.
    defaultPosition: { x: 260, y: 20 },
    defaultSize: { width: 440, height: 680 },
    minSize: { width: 380, height: 460 },
    maxSize: { width: 640, height: 960 },
    category: 'abilities',
    zIndex: 500,
    persist: false,
    persistGeometry: false,
    urlSeed: ['grounds'],
  },
  {
    kind: 'diagnostic',
    id: 'netobsPacketLog',
    label: 'Netobs — Packet Log',
    icon: '📡',
    description: 'Live per-packet log (in/out, type, bytes, seq) from NetworkTracer with type-stats expansion. (?netobs=1)',
    defaultPosition: { x: 360, y: 380 },
    defaultSize: { width: 520, height: 320 },
    minSize: { width: 360, height: 240 },
    maxSize: { width: 900, height: 800 },
    category: 'netcode',
    urlSeed: ['netobs'],
  },
  {
    kind: 'diagnostic',
    id: 'netobsLatencyHeatmap',
    label: 'Netobs — Latency Heatmap',
    icon: '📡',
    description: 'Per-message-type latency heatmap from netobs samples. (?netobs=1)',
    defaultPosition: { x: 900, y: 380 },
    defaultSize: { width: 480, height: 300 },
    minSize: { width: 360, height: 200 },
    maxSize: { width: 800, height: 600 },
    category: 'netcode',
    urlSeed: ['netobs'],
  },
  {
    kind: 'diagnostic',
    id: 'schemaGhost',
    label: 'Schema Ghost',
    icon: '👻',
    description: 'Renders a translucent ghost at the server schema position of the local player (drift visualizer). Side-effect toggle — no panel; flips the renderer __HELIX_SCHEMA_GHOST__ global while open. (?diag=1)',
    defaultPosition: { x: 0, y: 0 },
    defaultSize: { width: 0, height: 0 },
    category: 'netcode',
    urlSeed: ['diag', 'diagAll'],
  },
  {
    kind: 'diagnostic',
    id: 'raceRunPerf',
    label: 'Race Run Perf',
    icon: '🏁',
    description: 'Latest race-load-test run (M9 #46) — finish counts, avg/min/max times, per-participant table, msgs/sec (the G-D2 batching instrument), tick verdict, run history. Polls /api/diag/race/load-latest @5s; runs come from `make race-load-test`. (?raceperf=1)',
    defaultPosition: { x: 780, y: 100 },
    defaultSize: { width: 460, height: 520 },
    minSize: { width: 360, height: 280 },
    category: 'perf',
    zIndex: 500,
    urlSeed: ['raceperf'],
  },

  // ── gameplay kind: player action windows from QUICK MENU (persist:false) ──
  {
    kind: 'gameplay',
    id: 'settings',
    label: 'Game Settings',
    icon: '⚙️',
    description: 'Audio volumes + keybindings. Reads UserPrefs (storage) + KeyBindingManager — player-free.',
    defaultSize: { width: 760, height: 620 },
    minSize: { width: 500, height: 420 },
    maxSize: { width: 1200, height: 900 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'keyBindings',
    label: 'Key Bindings',
    icon: '⌨️',
    description: 'Keyboard & controller bindings',
    defaultSize: { width: 980, height: 640 },
    minSize: { width: 680, height: 420 },
    maxSize: { width: 1360, height: 900 },
    zIndex: 120,
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'twitchPiP',
    label: 'Live Stream',
    icon: '🔴',
    description: 'Twitch live-stream picture-in-picture embed (djmsqrvve). Player-free.',
    // The legacy TwitchPiPWindow passed no size/position → ToolWindow defaults
    // (400×500, centered). Set explicitly so the migration is geometry-faithful.
    defaultSize: { width: 400, height: 500 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'bank',
    label: 'Bank Storage',
    icon: '🏦',
    description: 'Vault ↔ inventory transfer near a banker over the authoritative bank protocol (BankHandler): typed gold amounts, double-click stacks. Tome look in the zone vibe (bank/BankView.tsx).',
    // Clear the fixed top-left player frame. 500×420 (lane AN): purse + five
    // whole 32 px slot rows per side, so a 20-slot bag or 24-slot vault shows whole.
    defaultPosition: { x: 260, y: 100 },
    defaultSize: { width: 500, height: 420 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'crafting',
    label: 'Crafting',
    icon: '⚒️',
    description: 'Server-authoritative crafting. Reads useCrafting + useInventory (InventorySystem singleton) — player-free.',
    // Geometry-faithful to the legacy CraftingWindow (defaultPosition 150,150 /
    // defaultSize 400×380, no minSize → host fallback).
    defaultPosition: { x: 150, y: 150 },
    defaultSize: { width: 400, height: 380 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'lobbyTools',
    label: 'Lobby Tools',
    icon: '🧰',
    description: 'Player-safe, rate-limited lobby actions. Reads NetworkManager singleton — player-free. Opened from the lobbyOnly QUICK MENU button.',
    // Geometry-faithful to the legacy LobbyToolsPanel.
    defaultPosition: { x: 120, y: 100 },
    defaultSize: { width: 320, height: 420 },
    minSize: { width: 280, height: 280 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'trade',
    label: 'Trade',
    icon: '🤝',
    description: 'Player-to-player trading (server-authoritative via useTrade). Reads useGame().otherPlayers (live) — keystone-free; no self-player read.',
    // Geometry-faithful to the legacy TradeWindow.
    defaultPosition: { x: 200, y: 100 },
    defaultSize: { width: 440, height: 440 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'party',
    label: 'Party',
    icon: '👥',
    description: 'Party roster + nearby-player invites. Reads useGame().otherPlayers (live) + useParty — keystone-free; no self-player read.',
    // Geometry-faithful to the legacy PartyPanel.
    defaultPosition: { x: 20, y: 150 },
    defaultSize: { width: 250, height: 350 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'jobs',
    label: 'Job Advancement',
    icon: '⚔️',
    description: 'Informational current-job snapshot + class-correct advancement preview. No server advancement action or persisted job authority.',
    // Geometry-faithful to the legacy JobsWindow (no defaultPosition → centered).
    defaultSize: { width: 400, height: 500 },
    minSize: { width: 320, height: 400 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'talents',
    label: 'Talent Tree',
    icon: '🌳',
    description: 'Class talent tree with server-authoritative ranks, available points, validation, and persistence. Respec is not available yet.',
    // Geometry-faithful to the legacy TalentsWindow (no defaultPosition → centered).
    defaultSize: { width: 500, height: 480 },
    minSize: { width: 380, height: 350 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'equipment',
    label: 'Character',
    icon: '🛡️',
    description: 'One character sheet for equipped gear, detailed stats, and server-authoritative stat allocation.',
    // Center the dossier by default. Character-only responsive CSS constrains
    // the frozen equipment-window chrome at short and phone-sized viewports.
    defaultSize: { width: 900, height: 660 },
    minSize: { width: 340, height: 420 },
    maxSize: { width: 960, height: 720 },
    zIndex: 500,
    category: 'game',
    persist: false,
    // Preserve the former stats-sheet diagnostic seed on the combined window.
    urlSeed: ['diag', 'diagAll'],
  },
  {
    kind: 'gameplay',
    id: 'inventory',
    label: 'Inventory',
    icon: '🎒',
    description: 'Tabbed item bag + gold. Live data from useInventory() (InventorySystem singleton); use-item via uiActionBridge.',
    // Geometry-faithful to the legacy InventoryUI (no defaultPosition → centered).
    defaultSize: { width: 340, height: 500 },
    minSize: { width: 280, height: 400 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'spells',
    label: 'Ability Browser',
    icon: '✨',
    description: 'Class-filtered spellbook + cast/preview. Reads useGame().player (live since the keystone); actions self-dispatch via ServiceRegistry/EventBus.',
    // Geometry-faithful to the legacy AbilityBrowser.
    // Clear the fixed top-center lobby/map banner (top 64–96px). The window
    // chrome is translucent, so overlap made both titles unreadable.
    defaultPosition: { x: 400, y: 110 },
    defaultSize: { width: 550, height: 450 },
    minSize: { width: 400, height: 300 },
    category: 'game',
    persist: false,
    // Preserves the URL-param half of the legacy `showSpells = useState(dashOpen)`
    // seed (?diag=1 / ?diagAll=1). The preset half (debugToolbox/full auto-open)
    // is dropped — it was a dev-preset convenience, not a URL contract.
    urlSeed: ['diag', 'diagAll'],
  },
  {
    kind: 'gameplay',
    id: 'quests',
    label: 'Quest Log',
    icon: '📜',
    description: 'Active quest log w/ progress + rewards. Reads useGame().player.quests (live since the keystone — the old mount passed no quests prop).',
    // Geometry-faithful to the legacy QuestUI (no defaultPosition → centered).
    defaultSize: { width: 420, height: 500 },
    minSize: { width: 320, height: 350 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'speedrunRecords',
    label: 'Coil Records',
    icon: '⏱️',
    description: 'Relight the Coil timer history, current ruleset, and permanent speedrun leaderboard.',
    defaultSize: { width: 620, height: 520 },
    minSize: { width: 480, height: 360 },
    maxSize: { width: 900, height: 800 },
    category: 'game',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'worldMap',
    label: 'World Map',
    icon: '🗺️',
    description: 'Explore the watercolor world atlas, find places and levels, and locate your current zone.',
    defaultSize: { width: 1040, height: 700 },
    minSize: { width: 340, height: 300 },
    category: 'game',
    persist: false, urlSeed: ['worldMap'],
  },
  {
    kind: 'gameplay',
    id: 'simulationPanel',
    label: 'Simulation Control',
    icon: '🧪',
    description: 'Bot/chaos simulation control (full preset only). SimulationSystem singleton; spawn-test via uiActionBridge. Player-free.',
    // Legacy used a dynamic right-edge position (window.innerWidth-350); the
    // registry is static so we let ToolWindow center it (minor position change).
    defaultSize: { width: 300, height: 450 },
    minSize: { width: 250, height: 350 },
    category: 'qa',
    persist: false,
    // Preserves the legacy `showSimulationPanel = useState(diagAllEnabled)` seed.
    urlSeed: ['diag', 'diagAll'],
  },
  {
    kind: 'gameplay',
    id: 'logViewer',
    label: 'Debug Console',
    icon: '📊',
    description: 'In-game log console (filter/copy/download). gameLogger singleton — player-free. VISUAL CHANGE: custom GlassPanel chrome folded into ToolWindow chrome.',
    defaultSize: { width: 640, height: 460 },
    minSize: { width: 360, height: 240 },
    category: 'debug',
    persist: false,
    // Reachable via ?logViewer=1 (the legacy console was HUD/DevTools-only; this
    // makes it URL-openable for dev + Visual QA).
    urlSeed: ['logViewer'],
  },
  {
    kind: 'gameplay',
    id: 'npcWindowViewer',
    label: 'NPC Window Viewer',
    icon: '🧪',
    description: 'Dev tool to browse/test NPC windows. NPCData/ITEM_DATABASE/questSystem singletons — player-free. VISUAL CHANGE: centered fixed-div panel folded into ToolWindow chrome.',
    defaultSize: { width: 760, height: 640 },
    minSize: { width: 520, height: 380 },
    category: 'qa',
    persist: false,
    // The legacy NPCWindowViewer had NO open trigger (unreachable). A urlSeed
    // makes the dev tool reachable via ?npcViewer=1 (and screenshot-able for QA).
    urlSeed: ['npcViewer'],
  },
  {
    kind: 'gameplay',
    id: 'devTools',
    label: 'Dev Tools',
    icon: '🛠️',
    description: 'Dev/debug suite (player/physics/classes/npcs/projectiles/connections tabs). Data from useGame()+useDebugConfig; actions via uiActionBridge. VISUAL CHANGE: custom drag/resize chrome folded into ToolWindow.',
    defaultSize: { width: 360, height: 600 },
    minSize: { width: 220, height: 200 },
    category: 'debug',
    persist: false,
    // Preserves the legacy `showDevTools = useState(diagAllEnabled)` seed.
    urlSeed: ['diag', 'diagAll'],
  },
  // Contract/QA dashboards — folded off the legacy UIContext flags (2026-06-10).
  // Opened from the 🛠 DebugToolbox via windowBridge.toggle (no longer auto-open
  // on ?diag; reach them from the button). Geometry preserved via
  // WindowHost.FROZEN_GAMEPLAY_TOOLWINDOW_ID.
  {
    kind: 'gameplay',
    id: 'qaPanel',
    label: 'QA Checklist',
    icon: '📋',
    description: 'Manual QA checklist sessions (Sprint/Ability/Full Game/Systems templates) with pass/fail tracking. Reads storage.',
    defaultPosition: { x: 60, y: 60 },
    defaultSize: { width: 420, height: 620 },
    minSize: { width: 340, height: 400 },
    category: 'qa',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'contractDashboard',
    label: 'Contract Dashboard',
    icon: '🟢',
    description: 'Runtime cross-system + ability contract validation (everything lights up green). Reads ServiceRegistry/EventBus + content health API.',
    defaultPosition: { x: 80, y: 40 },
    defaultSize: { width: 460, height: 580 },
    minSize: { width: 380, height: 300 },
    category: 'qa',
    persist: false,
  },
  // ── HUD fold (Phase 4, 2026-06-12) — the DEV-BAR windows HUD used to
  // mount behind local useState toggles (ratchet 6→0). Old ToolWindow ids
  // frozen in WindowHost.FROZEN_GAMEPLAY_TOOLWINDOW_ID so saved geometry
  // survives. EditorToolsWindow was deleted instead of migrated — the ✏️
  // DEV-BAR button is a pure editor-mode toggle (DJ ruling).
  {
    kind: 'gameplay',
    id: 'cameraControl',
    label: 'Camera',
    icon: '📷',
    description: 'Camera zoom / follow-speed / deadzone tuning with named profiles. GM-and-editor concern — the main game has no player zoom.',
    defaultPosition: { x: 940, y: 100 },
    defaultSize: { width: 280, height: 500 },
    minSize: { width: 250, height: 400 },
    category: 'world',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'simulationTools',
    label: 'Simulation & Tests',
    icon: '🧪',
    description: 'Scenario runner + perf benchmark (TestToolsTab). Distinct from Simulation Control (bot/chaos sim panel).',
    defaultPosition: { x: 100, y: 100 },
    defaultSize: { width: 400, height: 500 },
    minSize: { width: 350, height: 400 },
    category: 'qa',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'mapTools',
    label: 'Map Info',
    icon: '🗺️',
    description: 'Current map metadata + spawn/zone inspection (MapToolsTab).',
    defaultPosition: { x: 490, y: 150 },
    defaultSize: { width: 300, height: 400 },
    minSize: { width: 250, height: 300 },
    category: 'world',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'mapPicker',
    label: 'Map Picker',
    icon: '🧭',
    description: 'WC3-style map-select overlay — warps via requestMapSwitch, level-gated per destination. Dev travel surface (🛠 launcher / DEV BAR; never on helix2000.com, no player key — M is the World Map). The server refuses the warp in production and from non-GMs.',
    defaultSize: { width: 0, height: 0 },
    category: 'world',
    persist: false,
    presentation: 'overlay',
  },
  // GM windows (registry.gm.ts) and social windows (registry.social.ts) are split out for the ceiling.
  ...GM_WINDOW_DEFS, ...SOCIAL_WINDOW_DEFS,
] as const;
